
import { useState, useEffect } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import UsersSidebar from "@/components/users/UsersSidebar";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PermissionBadge } from "@/components/users/PermissionBadge";
import { PasswordForm } from "@/components/users/PasswordForm";
import { toast } from "sonner";
import { UserInfoCard } from "@/components/users/UserInfoCard";
import { ProfileForm } from "@/components/users/ProfileForm";

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefone: "",
    endereco: "",
    cidade: "",
    estado: "",
    cep: "",
    dataNascimento: "",
    bio: "",
    profilePicture: "",
  });

  // Load user data when component mounts
  useEffect(() => {
    if (user) {
      setFormData({
        nome: user.nome || "",
        email: user.email || "",
        telefone: user.telefone || "",
        endereco: user.endereco || "",
        cidade: user.cidade || "",
        estado: user.estado || "",
        cep: user.cep || "",
        dataNascimento: user.dataNascimento || "",
        bio: user.bio || "",
        profilePicture: user.profilePicture || "",
      });
      setIsLoading(false);
    }
  }, [user]);

  if (!user) {
    return <Navigate to="/login" />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const success = await updateUser(user.id, formData);
      if (success) {
        toast.success("Perfil atualizado com sucesso!");
      }
    } catch (error) {
      toast.error("Erro ao atualizar perfil");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const updateProfilePicture = async (base64Image: string) => {
    try {
      // Atualizar o estado local primeiro para feedback imediato ao usuário
      setFormData(prev => ({ ...prev, profilePicture: base64Image }));
      
      // Salvar no backend
      const success = await updateUser(user.id, { profilePicture: base64Image });
      return success;
    } catch (error) {
      console.error("Erro ao atualizar foto de perfil:", error);
      return false;
    }
  };

  const handlePasswordChange = () => {
    setShowPasswordDialog(true);
  };

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full">
        <UsersSidebar />
        <div className="flex-1 bg-gray-50 p-6 overflow-auto">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  Meu Perfil
                  {isLoading && (
                    <div className="h-4 w-4 rounded-full border-2 border-hero-blue border-t-transparent animate-spin ml-2"></div>
                  )}
                </h1>
                <p className="text-gray-600">Gerencie suas informações pessoais e configurações da conta</p>
              </div>
              <PermissionBadge role={user.role} size="md" />
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Sidebar com informações do usuário */}
              <div className="lg:col-span-1">
                <UserInfoCard 
                  user={{...user, profilePicture: formData.profilePicture}}
                  onPasswordChange={handlePasswordChange} 
                />
              </div>

              {/* Formulário principal */}
              <div className="lg:col-span-2">
                {isLoading ? (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 flex justify-center items-center h-[400px]">
                    <div className="flex flex-col items-center">
                      <div className="h-8 w-8 rounded-full border-3 border-hero-blue border-t-transparent animate-spin"></div>
                      <p className="mt-4 text-gray-500">Carregando dados do perfil...</p>
                    </div>
                  </div>
                ) : (
                  <ProfileForm 
                    formData={formData}
                    handleChange={handleChange}
                    handleSubmit={handleSubmit}
                    isSubmitting={isSubmitting}
                    updateProfilePicture={updateProfilePicture}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Alterar Senha</DialogTitle>
          </DialogHeader>
          <PasswordForm
            userId={user.id}
            onSuccess={() => {
              setShowPasswordDialog(false);
              toast.success("Senha alterada com sucesso!");
            }}
            onCancel={() => setShowPasswordDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
};

export default ProfilePage;
